import React from 'react';

import {callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import StructuredTableBulkAdapterErrorsModal from 'notebook/structured-tables/StructuredTableBulkAdapterErrorsModal';
import {getRegisterMergeOnlyChangeSummary} from 'samples/getInventoryRegistryChangeSummary';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';

export interface RegistrationErrorsModalProps {
  tagSchemaId: string;
  errorData: SpreadsheetValidationErrorInfo;
  canMerge: boolean;
  onRequestSubmit: (shouldMerge: boolean) => void;
  onRequestClose: () => void;
}

function primaryDuplicateItemIds(errorData: SpreadsheetValidationErrorInfo): Array<string> {
  return errorData.registryErrors.flatMap((error) =>
    error.primaryDuplicateItemApiId != null && typeof error.primaryDuplicateItemApiId === 'string'
      ? [error.primaryDuplicateItemApiId]
      : []
  );
}

function mergeAndRegisterWithDataMods(
  onRequestSubmit: (shouldMerge: boolean) => void,
  errorData: SpreadsheetValidationErrorInfo
): void {
  const duplicateItemIds = primaryDuplicateItemIds(errorData);
  callWithDataModifications({
    changeSummary: getRegisterMergeOnlyChangeSummary(duplicateItemIds.length),
    onPerformAction: () => {
      onRequestSubmit(true);
    },
    analyticsParams: {
      module: 'REGISTRY',
      actionOrigin: ActionOrigin.NOTEBOOK,
      modifiedAction: 'REGISTRY_MERGE',
      numModifiedTotalItems: duplicateItemIds.length,
      entryId: null,
    },
    apiIds: duplicateItemIds,
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

export default function RegistrationErrorsModal(props: RegistrationErrorsModalProps): JSX.Element {
  const mergeButton = {
    text: 'Continue',
    style: ButtonStyle.SECONDARY,
    onClick: () => {
      mergeAndRegisterWithDataMods(props.onRequestSubmit, props.errorData);
    },
    dataTestAttrs: {
      component: 'RegistrationErrorsModal',
      element: 'footerButton',
      key: ModalButtonKey.CONTINUE,
    },
  };

  const mergeWarning = (
    <span>
      Duplicates will be merged into existing registered entities. Only new entities which satisfy the
      uniqueness constraint will be registered.{' '}
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://help.benchling.com/hc/en-us/articles/9684256435597-Merge-duplicate-entities-into-the-Registry"
      >
        Learn more
      </a>
    </span>
  );

  return (
    <StructuredTableBulkAdapterErrorsModal
      title="Registration errors"
      extraButtons={props.canMerge ? [mergeButton] : null}
      errorData={props.errorData}
      onRequestClose={props.onRequestClose}
      tagSchemaId={props.tagSchemaId}
      extraErrorExplanation={props.canMerge ? mergeWarning : null}
    />
  );
}
